import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Eye, EyeOff, Pencil, Trash2, X, Loader2, Megaphone } from 'lucide-react'
import {
  adminListAnnonces, createAnnonce, updateAnnonce, deleteAnnonce,
  type Annonce, type AnnonceInput,
} from '../../api/annonces'
import { useEscapeKey } from '../../lib/useEscapeKey'
import { TexteRiche } from '../../lib/TexteRiche'

const vide: AnnonceInput = { titre: '', texte: '', date_debut: '', date_fin: '', actif: true, centre: false, ordre: 0 }

function periode(a: Annonce): string {
  const fr = (s: string | null) => (s ? s.slice(8, 10) + '/' + s.slice(5, 7) + '/' + s.slice(0, 4) : null)
  const d = fr(a.date_debut)
  const f = fr(a.date_fin)
  if (d && f) return `Du ${d} au ${f}`
  if (d) return `À partir du ${d}`
  if (f) return `Jusqu'au ${f}`
  return 'En permanence'
}

export function AdminAnnonces() {
  const qc = useQueryClient()
  const { data: annonces = [], isLoading } = useQuery({ queryKey: ['annonces-admin'], queryFn: adminListAnnonces })
  const [edit, setEdit] = useState<null | (AnnonceInput & { id?: number })>(null)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['annonces-admin'] })
    qc.invalidateQueries({ queryKey: ['annonces'] })
  }

  const saveMut = useMutation({
    mutationFn: (a: AnnonceInput & { id?: number }) => (a.id ? updateAnnonce(a.id, a) : createAnnonce(a)),
    onSuccess: () => { invalidate(); setEdit(null) },
  })
  const delMut = useMutation({ mutationFn: (id: number) => deleteAnnonce(id), onSuccess: invalidate })
  const toggleMut = useMutation({
    mutationFn: (a: Annonce) => updateAnnonce(a.id, { actif: !a.actif }),
    onSuccess: invalidate,
  })

  if (isLoading) {
    return <div className="grid place-items-center py-20 text-ink/50"><Loader2 className="h-6 w-6 animate-spin" /></div>
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink/55">
          {annonces.length} annonce(s) · {annonces.filter((a) => a.actif).length} active(s)
        </p>
        <button
          onClick={() => setEdit({ ...vide, ordre: annonces.length })}
          className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-dk"
        >
          <Plus className="h-4 w-4" /> Ajouter une annonce
        </button>
      </div>

      <div className="mt-4 space-y-3">
        {annonces.map((a) => (
          <div key={a.id} className={`rounded-xl border p-4 ${a.actif ? 'border-navy/10 bg-white' : 'border-navy/10 bg-sand/60 opacity-70'}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {a.titre && <p className="font-display font-semibold text-navy">{a.titre}</p>}
                <div className={`mt-0.5 space-y-1.5 text-sm leading-relaxed text-ink/75 ${a.centre ? 'text-center' : ''}`}><TexteRiche texte={a.texte} /></div>
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-ink/45">
                  <Megaphone className="h-3.5 w-3.5" /> {periode(a)} {a.actif ? '' : '· masquée'}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                <button onClick={() => toggleMut.mutate(a)} title={a.actif ? 'Masquer' : 'Afficher'} className="rounded-lg p-2 text-ink/50 hover:bg-sand hover:text-navy">
                  {a.actif ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
                <button onClick={() => setEdit({ id: a.id, titre: a.titre, texte: a.texte, date_debut: a.date_debut || '', date_fin: a.date_fin || '', actif: a.actif, centre: a.centre, ordre: a.ordre })} title="Modifier" className="rounded-lg p-2 text-ink/50 hover:bg-sand hover:text-navy">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => { if (confirm('Supprimer cette annonce ?')) delMut.mutate(a.id) }} title="Supprimer" className="rounded-lg p-2 text-ink/50 hover:bg-red-50 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {annonces.length === 0 && <p className="py-10 text-center text-sm text-ink/40">Aucune annonce.</p>}
      </div>

      {edit && (
        <AnnonceModal value={edit} busy={saveMut.isPending} onChange={setEdit} onClose={() => setEdit(null)} onSave={() => saveMut.mutate(edit)} />
      )}
    </div>
  )
}

function AnnonceModal({
  value, busy, onChange, onClose, onSave,
}: {
  value: AnnonceInput & { id?: number }
  busy: boolean
  onChange: (v: AnnonceInput & { id?: number }) => void
  onClose: () => void
  onSave: () => void
}) {
  useEscapeKey(onClose)
  const champ = 'w-full rounded-xl border border-navy/15 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-orange'
  const label = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink/55'
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-ink/50 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Annonce" className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-soft" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-navy">{value.id ? "Modifier l'annonce" : 'Nouvelle annonce'}</h3>
          <button onClick={onClose} aria-label="Fermer" className="rounded-lg p-2 text-ink/50 hover:bg-sand"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <label className={label}>Titre (facultatif)</label>
            <input value={value.titre} onChange={(e) => onChange({ ...value, titre: e.target.value })} className={champ} />
          </div>
          <div>
            <label className={label}>Texte de l'annonce</label>
            <textarea rows={6} value={value.texte} onChange={(e) => onChange({ ...value, texte: e.target.value })} className={champ} />
            <p className="mt-1 text-xs text-ink/55">
              Une ligne vide sépare deux paragraphes · <code>**gras**</code> met en gras.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={label}>Afficher à partir du</label>
              <input type="date" value={value.date_debut || ''} onChange={(e) => onChange({ ...value, date_debut: e.target.value })} className={champ} />
            </div>
            <div>
              <label className={label}>Jusqu'au</label>
              <input type="date" value={value.date_fin || ''} onChange={(e) => onChange({ ...value, date_fin: e.target.value })} className={champ} />
            </div>
          </div>
          <p className="text-xs text-ink/45">Laissez les dates vides pour afficher en permanence.</p>
          <label className="flex items-center gap-2 text-sm text-ink/75">
            <input type="checkbox" checked={value.actif} onChange={(e) => onChange({ ...value, actif: e.target.checked })} className="h-4 w-4 accent-navy" />
            Afficher cette annonce
          </label>
          <label className="flex items-center gap-2 text-sm text-ink/75">
            <input type="checkbox" checked={!!value.centre} onChange={(e) => onChange({ ...value, centre: e.target.checked })} className="h-4 w-4 accent-navy" />
            Centrer le texte dans le bandeau
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-medium text-ink/60 hover:bg-sand">Annuler</button>
          <button onClick={onSave} disabled={busy || !value.texte.trim()} className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2 text-sm font-semibold text-white hover:bg-navy-dk disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  )
}
